import { Box, Divider, Paper, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { dynamicInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { formatPrice } from '@vy/services';
import type { Currency, RevenueBreakdown } from '@vy/types';
import { QuestionMarkCircle } from '@vy/ui-design-system';
import { isNotNullOrUndefined } from '@vy/utils';
import { Price } from '../../../../components/design/Price';
import { VyTooltip } from '../../../../components/new-design/VyTooltip/VyTooltip';

type DynamicInvoiceSummaryProps = {
  revenueBreakdown: RevenueBreakdown;
  currency: Currency;
  taxRate?: number;
  isTaxIncludedInTotal?: boolean;
  remainingCreditBalanceAfterApproval?: number;
};

export const DynamicInvoiceSummary: React.FC<DynamicInvoiceSummaryProps> = ({
  revenueBreakdown,
  currency,
  taxRate = 0,
  isTaxIncludedInTotal = false,
  remainingCreditBalanceAfterApproval,
}) => {
  const formattedTaxRate = taxRate > 0 ? ` ${taxRate}%` : '';

  return (
    <Stack
      sx={{
        rowGap: '16px',
      }}
    >
      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
        }}
      >
        Summary
      </Typography>
      <Paper
        sx={{
          padding: '20px 24px',
          borderRadius: '10px',
          rowGap: '16px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          sx={{
            rowGap: '8px',
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: 'start',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body1">Sub total</Typography>
            <Price variant="h6" currency={currency} fractionDigits={2}>
              {revenueBreakdown.subtotal}
            </Price>
          </Stack>
          <Stack
            direction="row"
            {...getSelectorAttribute(dynamicInvoicePageSelectors.summary.discount)}
            sx={{
              alignItems: 'start',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body1">Discount</Typography>
            <Price variant="h6" currency={currency} fractionDigits={2}>
              {-revenueBreakdown.discount}
            </Price>
          </Stack>
        </Stack>
        {!isTaxIncludedInTotal && (
          <Stack
            direction="row"
            sx={{
              alignItems: 'start',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body1">Tax {formattedTaxRate}</Typography>
            <Price variant="h6" currency={currency} fractionDigits={2}>
              {revenueBreakdown.tax}
            </Price>
          </Stack>
        )}
        {revenueBreakdown.creditsUsed > 0 && (
          <>
            <Stack
              direction="row"
              sx={{
                alignItems: 'start',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                Total
              </Typography>
              <Price variant="h6" currency={currency} fractionDigits={2} sx={{ fontWeight: 600 }}>
                {revenueBreakdown.subtotal}
              </Price>
            </Stack>
            <Stack
              direction="row"
              sx={{
                alignItems: 'start',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography variant="body1">Credits redeem</Typography>
                {isNotNullOrUndefined(remainingCreditBalanceAfterApproval) && (
                  <VyTooltip
                    placement="top"
                    tooltipContent={`Remaining credits after invoice's approval ${formatPrice(remainingCreditBalanceAfterApproval, currency, 0, 0)}`}
                  >
                    <Box component="span" sx={{ display: 'flex', cursor: 'pointer' }}>
                      <QuestionMarkCircle />
                    </Box>
                  </VyTooltip>
                )}
              </Box>
              <Price variant="h6" currency={currency}>
                {-revenueBreakdown.creditsUsed}
              </Price>
            </Stack>
          </>
        )}

        <Divider />

        <Stack
          direction="row"
          {...getSelectorAttribute(dynamicInvoicePageSelectors.summary.totalAmount)}
          sx={{
            alignItems: 'start',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">Amount due {isTaxIncludedInTotal ? ' (tax incl.)' : ''}</Typography>
          <Price variant="h4" currency={currency} fractionDigits={2}>
            {revenueBreakdown.total}
          </Price>
        </Stack>
      </Paper>
    </Stack>
  );
};
